import {
  Component,
  signal,
  computed,
  OnInit,
  inject,
  ChangeDetectionStrategy,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  ReactiveFormsModule,
  FormBuilder,
  FormGroup,
  Validators,
  AbstractControl,
  ValidatorFn,
} from '@angular/forms';

import {
  BreadCrumb,
  BreadcrumbComponent,
  PageLayoutComponent,
  ButtonComponent,
  InputFieldComponent,
  SelectComponent,
  TextareaComponent,
  FileUploadZoneComponent,
  FileData,
  UploadedFile,
  WizardStepsComponent,
  CollectionHeaderComponent,
  StatusBadgeComponent,
  StepperNumberComponent,
} from '@mic-ac/ui-kit';

import { FormOptionsService } from '../../../../core/services/wizard-form-data/wizard-page-data';

import { WizardFormSubmitService } from '../../../../core/services/wizard-form-submit/wizard-form-submit.service';
import { AuthServiceService } from '../../../../core/services/auth-service/auth-service.service';
import { ResponseUser } from '../../../../shared/models/utente';
import {
  mainSrc,
  ProprietarioCollezione,
  wizardResponse,
  IndirizzoSuggestion,
} from '../../../../shared/models/wizardData';

import { ActivatedRoute, Router } from '@angular/router';
import { SnackbarService } from '@mic-ac/ui-kit';
import { merge, startWith, Subject, takeUntil } from 'rxjs';
import {
  COLLECTION_IMAGE_MAX_COUNT,
  COLLECTION_IMAGE_MAX_SIZE_BYTES,
  collectionImageSizeErrorMessage,
  collectionImageTypeErrorMessage,
  validateCollectionImageFiles,
} from '../../../../core/utils/collection-image-upload';


type StepKey = 'anagrafici' | 'digitale' | 'fisico' | 'mappa';
type Step = { key: StepKey; label: string };



@Component({
    selector: 'app-entity-wizard-page',
    imports: [
        CommonModule,
        ReactiveFormsModule,
        PageLayoutComponent,
        ButtonComponent,
        InputFieldComponent,
        SelectComponent,
        TextareaComponent,
        FileUploadZoneComponent,
        WizardStepsComponent,
        CollectionHeaderComponent,
        StatusBadgeComponent,
        BreadcrumbComponent,
        StepperNumberComponent,
    ],
    templateUrl: './entity-wizard-page.component.html',
    styleUrl: './entity-wizard-page.component.css',
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class EntityWizardPageComponent implements OnInit {

  private destroy$ = new Subject<void>();

  private toggleRequired(ctrl: AbstractControl | null, on: boolean) {
    if (!ctrl) return;
    if (on) {
      ctrl.enable({ emitEvent: false });
      ctrl.addValidators(Validators.required);
    } else {
      ctrl.removeValidators(Validators.required);
      ctrl.setErrors(null);
      ctrl.disable({ emitEvent: false });
    }
    ctrl.updateValueAndValidity({ emitEvent: false });
  }

  // select dove 0 è un valore valido:
  private requiredAllowZero: ValidatorFn = (c) => (c.value === 0 || !!c.value) ? null : { required: true };

  readonly maxImageSizeBytes = COLLECTION_IMAGE_MAX_SIZE_BYTES;
  private readonly maxCollectionImages = COLLECTION_IMAGE_MAX_COUNT;

  private readonly onlyIfDef = <T extends object>(val: any, obj: T): Partial<T> =>
    val !== undefined && val !== null ? obj : {};

  private parseCondivisaCon(value?: string | null): string[] {
    if (!value) return [];
    const allowed = new Set(this.condivisaConOptions.map(o => o.value));
    return value
      .split(/[,;]/)
      .map(item => item.trim())
      .filter(Boolean)
      .map(item => this.condivisaConOptions.find(o => o.value === item || o.label === item)?.value ?? item)
      .filter(item => allowed.has(item));
  }

  private serializeCondivisaCon(value: string | string[] | null | undefined): string | null {
    const items = Array.isArray(value)
      ? value.map(item => String(item).trim()).filter(Boolean)
      : this.parseCondivisaCon(value);
    return items.length ? items.join(', ') : null;
  }

  private getFullTipologica(
    type: 'proprietario' | 'categoriaproprietario' | 'tipobene' | 'metadato' | 'licenza' | 'dominio' | 'catalogazione' | 'statoavanzamento',
    code: string | string[] | null | undefined
  ): any {
    if (!code) return null;

    const getOptions = () => {
      switch (type) {
        case 'proprietario': return this.tipologie;
        case 'categoriaproprietario': return this.categorie;
        case 'tipobene': return this.tipologieBeni;
        case 'metadato': return this.tipologiaRisorse;
        case 'licenza': return this.licenza;
        case 'dominio': return this.dominio;
        case 'catalogazione': return this.catalogato;
        case 'statoavanzamento': return this.statoAvanzamento;
      }
    };

    const options = getOptions()();
    if (Array.isArray(code)) {
      return code.map(c => options.find(o => o.value === c)?.raw ?? { codice: c });
    }
    return options.find(o => o.value === code)?.raw ?? { codice: code };
  }

  /**
   * Costruisce il payload indirizzo per il BE.
   * Se l'utente ha selezionato un suggerimento (id presente), usa l'oggetto completo
   * da storedIndirizzi (con latitudine/longitudine). Altrimenti costruisce dai campi form.
   */
  private resolveIndirizzoPayload(
    id: number | null,
    indirizzo: string, civico: string, cap: string, comune: string, provincia: string
  ): any {
    if (id) {
      const stored = this.storedIndirizzi().find(i => i.value === id);
      if (stored?.data) return stored.data;
    }
    return {
      ...(id ? { id } : {}),
      ...(indirizzo ? { indirizzo } : {}),
      ...(civico ? { civico } : {}),
      ...(cap ? { cap } : {}),
      ...(comune ? { comune } : {}),
      ...(provincia ? { provincia } : {}),
    };
  }

  private addStoredIndirizzo(indir: any): void {
    if (!indir?.id) return;
    const label = this.buildIndirizzoLabel(indir);
    if (!label) return;
    const existingById = this.storedIndirizzi().find((i: any) => i.value === indir.id);
    if (existingById) {
      // Aggiorna label e data se il contenuto è cambiato
      if (existingById.label !== label) {
        this.storedIndirizzi.update(list =>
          list.map(i => i.value === indir.id ? { value: indir.id, label, data: indir } : i)
        );
      }
      return;
    }
    // Evita doppioni visivi: stesso testo ma id diverso → aggiorna l'id e i dati
    const existingByLabel = this.storedIndirizzi().find((i: any) => i.label === label);
    if (existingByLabel) {
      this.storedIndirizzi.update(list =>
        list.map(i => i.label === label ? { value: indir.id, label, data: indir } : i)
      );
      return;
    }
    this.storedIndirizzi.update(list => [...list, { value: indir.id, label, data: indir }]);
  }

  private buildIndirizzoLabel(indir: any): string {
    return indir?.indirizzoEsteso
      ?? [indir?.indirizzo, indir?.civico, indir?.cap, indir?.comune, indir?.provincia]
          .filter(Boolean).join(', ')
      ?? '';
  }

  private resolveSelectedAddress(
    selection: IndirizzoSuggestion | string | null,
    suggestions: IndirizzoSuggestion[]
  ): IndirizzoSuggestion | null {
    if (!selection) return null;
    if (typeof selection !== 'string') return selection;

    const normalized = selection.trim().toLowerCase();
    return suggestions.find(suggestion => {
      const street = suggestion.indirizzo?.trim().toLowerCase();
      const extended = suggestion.indirizzoEsteso?.trim().toLowerCase();
      return street === normalized || extended === normalized;
    }) ?? null;
  }

  // address suggestion helper
  onAddressSearchSelect(
    selection: IndirizzoSuggestion | string,
    suggestions: IndirizzoSuggestion[],
    form: FormGroup,
    fields: { addr: string, civ?: string, cap: string, city: string, prov: string, id?: string }
  ) {
    const suggestion = this.resolveSelectedAddress(selection, suggestions);
    if (!suggestion) return;

    form.patchValue({
      [fields.addr]: suggestion.indirizzoEsteso ?? suggestion.indirizzo ?? '',
      ...(fields.civ ? { [fields.civ]: suggestion.civico ?? '' } : {}),
      [fields.cap]: suggestion.cap ?? '',
      [fields.city]: suggestion.comune ?? '',
      [fields.prov]: suggestion.provincia ?? '',
      ...(fields.id ? { [fields.id]: suggestion.id ?? null } : {}),
    });

    if (suggestion.id) {
      this.addStoredIndirizzo(suggestion);
    }
  }

  addressSuggestionsPatrimonio = signal<IndirizzoSuggestion[]>([]);
  addressSuggestionsSedeLegale = signal<IndirizzoSuggestion[]>([]);

  private searchAddress(term: string, target: 'patrimonio' | 'sedeLegale') {
    const targetSignal = target === 'patrimonio' ? this.addressSuggestionsPatrimonio : this.addressSuggestionsSedeLegale;
    if (!term || term.length < 3) {
      targetSignal.set([]);
      return;
    }
    this.submitLogic.indirizzoSuggestion(term).subscribe(suggestions => {
      targetSignal.set(suggestions);
    });
  }

  searchAddressPatrimonio(term: string) {
    this.searchAddress(term, 'patrimonio');
  }

  searchAddressSedeLegale(term: string) {
    this.searchAddress(term, 'sedeLegale');
  }

  constructor(private fb: FormBuilder) { }
  user!: ResponseUser;

  // service
  private opts = inject(FormOptionsService);
  private submitLogic = inject(WizardFormSubmitService);
  private authService = inject(AuthServiceService);
  private snackbar = inject(SnackbarService);
  private router = inject(Router);
  private route = inject(ActivatedRoute);

  // select
  tipologie = this.opts.optionsSignal('proprietario');
  categorie = this.opts.optionsSignal('categoriaproprietario');
  tipologieBeni = this.opts.optionsSignal('tipobene');
  tipologiaRisorse = this.opts.optionsSignal('metadato');
  licenza = this.opts.optionsSignal('licenza');
  dominio = this.opts.optionsSignal('dominio');
  catalogato = this.opts.optionsSignal('catalogazione');
  statoAvanzamento = this.opts.optionsSignal('statoavanzamento');

  accessibileOnline = this.opts.yesNo;
  accessibileLoco = this.opts.yesNo;
  soluzioniNazionali = this.opts.yesNo;
  nativaDigitale = this.opts.yesNo;
  proprietarioDiverso = this.opts.yesNo;
  mappa = this.opts.yesNo;
  condivisaConOptions = [
    { value: 'SBN', label: 'SBN' },
    { value: 'MOL', label: 'MOL' },
    { value: 'SAN-SIA', label: 'SAN-SIA' },
    { value: 'SigecWeb-Clio', label: 'SigecWeb-Clio' },
    { value: 'No', label: 'No' },
  ];
  // utils

  storedIndirizzi = signal<{ value: any; label: string; data?: any }[]>([]);
  idCollezioneModal = this.submitLogic.idCollezioneModal;
  collectionDescrizione = signal<string>('');

  // address edit mode: true = mostra search API, false = mostra readonly
  editingAddressPatrimonio = signal(true);
  editingAddressSedeLegale = signal(true);

  idProprietario = signal(0);
  isEditMode = signal(false);
  file = signal<string | null>(null);
  mappaCompleted = signal(false);
  proprietario!: ProprietarioCollezione;
  mainRes!: wizardResponse;

  // breadcrumbs
  breadcrumbs: BreadCrumb[] = [
    { label: 'Gestione collezioni', fn: () => this.router.navigate(['/mp/my-area']) },
    { label: 'Dettaglio collezione' },
  ];

  // wizard logics and data
  steps = signal<Step[]>([]);

  idx = signal(0);
  current = computed(() => this.steps()[this.idx()]);
  stepLabels = computed(() => this.steps().map(s => s.label));

  savedSteps = signal<Set<StepKey>>(new Set());

  private get stepConfig(): Record<StepKey, { form: () => FormGroup; setup: () => void }> {
    return {
      anagrafici: { form: () => this.formDataAnagrafica, setup: () => this.setupProprietario() },
      digitale:   { form: () => this.formDataDigitale,  setup: () => this.setupCollezioneDigitale() },
      fisico:     { form: () => this.patrimonioFisico,  setup: () => this.setupPatrimonioFisico() },
      mappa:      { form: () => this.visualizzazioneMappa, setup: () => this.setupVisMappa() },
    };
  }

  onTabChange(label: string): void {
    const targetIndex = this.steps().findIndex(s => s.label === label);
    if (targetIndex < 0 || targetIndex === this.idx()) return;

    // Navigazione indietro: libera, senza salvataggio
    if (targetIndex < this.idx()) {
      this.idx.set(targetIndex);
      return;
    }

    // Navigazione in avanti: valida e salva lo step corrente
    const key = this.current().key;
    const step = this.stepConfig[key];
    if (step) {
      const form = step.form();
      form.markAllAsTouched();
      if (form.invalid) {
        const invalidNames = Object.entries(form.controls)
          .filter(([, c]) => c.invalid)
          .map(([name]) => this.fieldLabels[name] ?? name);

        this.snackbar.show(
          `Campi obbligatori mancanti: ${invalidNames.join(', ')}`,
          'error'
        );
        return;
      }
      this.savedSteps.update(s => { const next = new Set(s); next.add(key); return next; });
      step.setup();
    }
    this.idx.set(targetIndex);
  }

  uploadedFiles = signal<FileData[]>([]);

  /** dataUrl completo della copertina — aggiorna l'header immediatamente all'upload */
  coverImagePreview = computed<string | null>(() => {
    const cover = this.uploadedFiles().find(f => !!(f as FileData & { isCover?: boolean }).isCover);
    return (cover as (FileData & { thumbnail?: string }) | undefined)?.thumbnail ?? this.file() ?? null;
  });

  toggleFisico(formValueFisico: string) {
    this.steps.update((arr) => {
      const hasFisico = arr.some((s) => s.key === 'fisico');
      const idxDigitale = arr.findIndex((s) => s.key === 'digitale');
      const shouldShow = formValueFisico === 'no';

      if (shouldShow && !hasFisico) {
        const insertAt = idxDigitale >= 0 ? idxDigitale + 1 : arr.length;
        return [
          ...arr.slice(0, insertAt),
          { key: 'fisico', label: 'Informazioni patrimonio' } as Step,
          ...arr.slice(insertAt),
        ];
      }
      if (!shouldShow && hasFisico) {
        this.savedSteps.update(s => { const n = new Set(s); n.delete('fisico'); return n; });
        return arr.filter((s) => s.key !== 'fisico');
      }
      return arr;
    });
  }

  private readonly fieldLabels: Record<string, string> = {
    // step 1
    propNome: 'Nominativo',
    tipologia: 'Tipologia',
    categoria: 'Categoria',
    indirizzo: 'Indirizzo (sede legale)',
    civico: 'Civico',
    cap: 'CAP',
    comune: 'Comune',
    provincia: 'Provincia',
    // step 2
    titolo: 'Titolo',
    descrizione: 'Descrizione',
    tipologiaBeni: 'Tipologia di beni digitalizzati',
    tipologiaRisorseDigitali: 'Tipologia di risorse digitali',
    accessibileOnline: 'Accessibile online',
    url: 'URL',
    accessibileLoco: 'Accessibile in loco',
    modalitaAccesso: 'Modalità di accesso in loco',
    patrimonioNonAccessibile: 'Patrimonio non accessibile',
    soluzioniNazionali: 'Soluzioni nazionali',
    nativaDigitale: 'Collezione nativa digitale',
    // step 3
    descrizioneFisico: 'Descrizione patrimonio',
    dominio: 'Dominio',
    catalogatoInventariatoStato: 'Stato di descrizione',
    proprietarioDiverso: 'Proprietario diverso',
    condivisaCon: 'Condivisa con i sistemi centrali',
    sistemiInformatizzati: 'Sistema descrittivo informatizzato',
    inventariCartacei: 'Strumenti di corredo',
    propNomeFisico: 'Nome proprietario fisico',
    propCognomeFisico: 'Cognome proprietario fisico',
    tipologiaFisico: 'Tipologia proprietario fisico',
    categoriaFisico: 'Categoria proprietario fisico',
    // step 4
    collezioneMappa: 'Mostrare la collezione in mappa',
    indirizzoMappa: 'Indirizzo mappa',
  };

  // Salvataggio di sicurezza dello step corrente (pulsante "Salva")
  saveCurrentStep(): void {
    const key = this.current().key;
    const step = this.stepConfig[key];
    if (!step) return;
    const form = step.form();
    form.markAllAsTouched();
    if (form.invalid) {
      const invalidNames = Object.entries(form.controls)
        .filter(([, c]) => c.invalid)
        .map(([name]) => this.fieldLabels[name] ?? name);
      console.warn(`Step "${key}" invalido. Controlli:`, invalidNames);
      this.snackbar.show(
        `Campi obbligatori mancanti: ${invalidNames.join(', ')}`,
        'error'
      );
      return;
    }
    this.savedSteps.update(s => { const next = new Set(s); next.add(key); return next; });
    step.setup();
  }

  // forms data

  formDataAnagrafica!: FormGroup;
  formDataDigitale!: FormGroup;
  visualizzazioneMappa!: FormGroup;
  patrimonioFisico!: FormGroup;

  ngOnInit(): void {
    // Se il signal è ancora al valore di default (1), significa che la pagina è stata
    // ricaricata (refresh) senza una navigazione legittima dal genitore → redirect alla tabella.
    if (this.idCollezioneModal() === 1) {
      this.router.navigate(['/mp/my-area']);
      return;
    }

    this.getTempUser();

    // anagrafica
    this.formDataAnagrafica = this.fb.group({
      // SOGGETTO COMPILATORE
      nome: [''],
      cognome: [''],
      email: [''],
      enteAppartenenza: [''],
      professioneRuolo: [''],

      // PROPRIETARIO DELLA COLLEZIONE DIGITALE
      propNome: ['', Validators.required],
      propCognome: [''],
      tipologia: this.fb.control('', {
        validators: Validators.required,
      }),
      categoria: this.fb.control('', {
        validators: Validators.required,
      }),
      codiceIdentificativo: [''  ],
      indirizzo: ['', Validators.required],
      civico: [''],
      cap: ['', [Validators.required, Validators.pattern(/^\d{5}$/)]],
      comune: ['', Validators.required],
      provincia: ['', Validators.required],
      sitoWeb: [
        '',
        [
          // opzionale
          Validators.pattern(
            /^(https?:\/\/)?([^\s.]+\.[^\s]{2,}|localhost)(\/\S*)?$/
          ),
        ],
      ],
    });

    // digitale
    this.formDataDigitale = this.fb.group({
      titolo: ['', Validators.required],
      descrizione: ['', Validators.required],
      tipologiaBeni: [[], Validators.required],
      tipologiaRisorseDigitali: [[], Validators.required],
      presenzaMetadati: [''],
      note: [''],
      accessibileOnline: ['', Validators.required],
      url: ['', Validators.pattern(/^https?:\/\/.+/i)],
      accessibileLoco: ['', Validators.required],
      modalitaAccesso: [''],
      patrimonioNonAccessibile: [''],
      soluzioniNazionali: ['', Validators.required],
      licenza: [''],
      progetto: [''],
      periodoFrom: [''],
      periodoTo: [''],
      statoAvanzamento: [''],
      nativaDigitale: ['', Validators.required],
    });
    // mappa
    this.visualizzazioneMappa = this.fb.group({
      collezioneMappa: ['', Validators.required],
      indirizzoMappa: [''],
    });

    // informazioni patrimonio fisico
    this.patrimonioFisico = this.fb.group({
      descrizioneFisico: ['', Validators.required],
      dominio: ['', Validators.required],
      proprietarioDiverso: ['', Validators.required],
      condizioniAccesso: [''],
      //INDIRIZZO patrimonio fisico
      indirizzoPatrimonioFisico: [''],
      indirizzoPatrimonioFisicoId: [null],
      civicoPatrimonioFisico: [''],
      capPatrimonioFisico: ['', Validators.pattern(/^\d{5}$/)],
      comunePatrimonioFisico: [''],
      provinciaPatrimonioFisico: [''],
      // CATALOGATO INVENTARIATO
      catalogatoInventariatoStato: ['',Validators.required],
      // facoltativi, mostrati solo quando catalogatoInventariatoStato !== 'CA002'
      condivisaCon: [[]],
      sistemiInformatizzati: [''],
      inventariCartacei: [''],
      // PROPRIETARIO DEL PATRIMONIO FISICO
      // richiesti condizionalmente (solo quando proprietarioDiversoSelect === 0) — validator aggiunto via toggleRequired
      propNomeFisico: [''],
      proprietarioDiversoSelect: [null],
      propCognomeFisico: [''],
      tipologiaFisico: [null],
      categoriaFisico: [null],
      codiceIdentificativoFisico: [''],
      indirizzoSedeLegaleFisico: [''],
      indirizzoSedeLegaleFisicoId: [null],
      civicoPatrimonioProprietario: [''],
      capPatrimonioProprietario: ['', Validators.pattern(/^\d{5}$/)],
      comunePatrimonioProprietario: [''],
      provinciaPatrimonioProprietario: [''],
      sitoWebProprietario: [
        '',
        [ Validators.pattern(/^https?:\/\/.+/i)],
      ],
    });
    this.disableFirstSection();
    // form data scorciatoie
    const fA = this.formDataAnagrafica;
    const fD = this.formDataDigitale;
    const fF = this.patrimonioFisico;

    //  propCognome richiesto solo se tipologia === 'PR004'
    const tipologiaCtrl = fA.get('tipologia');
    const propCognome = fA.get('propCognome');

    tipologiaCtrl?.valueChanges
      .pipe(startWith(tipologiaCtrl.value), takeUntil(this.destroy$))
      .subscribe((val) => {
        const need = String(val) === 'PR004';
        this.toggleRequired(propCognome, need);

        // se il campo è nascosto, resetta il valore
        if (!need) {
          propCognome?.setValue('');
          propCognome?.markAsPristine();
        }
        propCognome?.updateValueAndValidity({ emitEvent: false });
      });

    //  URL richiesto solo se accessibileOnline === 'si'
    const onlineCtrl = fD.get('accessibileOnline');
    const urlCtrl = fD.get('url');

    onlineCtrl?.valueChanges
      .pipe(startWith(onlineCtrl.value), takeUntil(this.destroy$))
      .subscribe((v) => {
        const isSi = v === 'si';
        this.toggleRequired(urlCtrl, isSi);
        if (!isSi) {
          urlCtrl?.setValue('');
          urlCtrl?.markAsPristine();
        }
        urlCtrl?.updateValueAndValidity({ emitEvent: false });
      });

    //  modalitaAccesso richiesta solo se accessibileLoco === 'si'
    const locoCtrl = fD.get('accessibileLoco');
    const modoAcceso = fD.get('modalitaAccesso');

    const patrimonioNonAccessibileCtrl = fD.get('patrimonioNonAccessibile');

    locoCtrl?.valueChanges
      .pipe(startWith(locoCtrl.value), takeUntil(this.destroy$))
      .subscribe((v) => {
        const isSi = v === 'si';
        const isNo = v === 'no';

        // Gestione Modalità di accesso (solo se SI)
        this.toggleRequired(modoAcceso, isSi);
        if (!isSi) {
          modoAcceso?.setValue('');
          modoAcceso?.markAsPristine();
        }

        // Gestione Patrimonio non accessibile (solo se NO)
        this.toggleRequired(patrimonioNonAccessibileCtrl, isNo);
        if (!isNo) {
          patrimonioNonAccessibileCtrl?.setValue('');
          patrimonioNonAccessibileCtrl?.markAsPristine();
        }

        // Forza ricalcolo validità per entrambi per evitare "residui" di errori
        modoAcceso?.updateValueAndValidity({ emitEvent: false });
        patrimonioNonAccessibileCtrl?.updateValueAndValidity({ emitEvent: false });
      });

    //  nativaDigitale: mostra step 'fisico' solo se valore === 'no'
    const nativaDigitaleCtrl = fD.get('nativaDigitale');
    nativaDigitaleCtrl?.valueChanges
      .pipe(startWith(nativaDigitaleCtrl.value), takeUntil(this.destroy$))
      .subscribe((v) => {
        if (v) this.toggleFisico(v);
      });

    // --- 4) blocco "Proprietario patrimonio fisico" (quando selezioni "Nuovo" con valore 0)
    const propDivSel = fF.get('proprietarioDiversoSelect');
    const propDiversoCtrl = fF.get('proprietarioDiverso');

    const campiPropFisico = [
      fF.get('propNomeFisico'),
      fF.get('tipologiaFisico'),
      fF.get('categoriaFisico'),
      fF.get('indirizzoSedeLegaleFisico'),
      fF.get('civicoPatrimonioProprietario'),
      fF.get('capPatrimonioProprietario'),
      fF.get('comunePatrimonioProprietario'),
      fF.get('provinciaPatrimonioProprietario'),
      // sitoWebProprietario è opzionale => lascia senza required
      // propCognomeFisico: gestito separatamente (solo se tipologiaFisico === 'PR004')
    ];

    // clearValues=true → svuota i campi; clearValues=false → rimuove solo required (lascia valori per VIEW)
    const abilitaCampiProprietarioFisico = (need: boolean, clearValues = true) => {
      campiPropFisico.forEach(c => {
        this.toggleRequired(c, need);
        if (!need && clearValues) {
          c?.setValue('');
          c?.markAsPristine();
        }
        c?.updateValueAndValidity({ emitEvent: false });
      });
      // cognome: gestito separatamente
      const tipologia = fF.get('tipologiaFisico')?.value;
      this.toggleRequired(fF.get('propCognomeFisico'), need && String(tipologia) === 'PR004');
      if (!need && clearValues) {
        fF.get('propCognomeFisico')?.setValue('');
        fF.get('propCognomeFisico')?.markAsPristine();
      }
      fF.get('propCognomeFisico')?.updateValueAndValidity({ emitEvent: false });
    };

    // Attiva requiredAllowZero su proprietarioDiversoSelect solo se proprietarioDiverso === 'si'
    propDiversoCtrl?.valueChanges
      .pipe(startWith(propDiversoCtrl.value), takeUntil(this.destroy$))
      .subscribe((v) => {
        const show = v === 'si';
        if (show) {
          propDivSel?.addValidators(this.requiredAllowZero);
        } else {
          propDivSel?.removeValidators(this.requiredAllowZero);
          propDivSel?.setValue(null);
          propDivSel?.markAsPristine();
          abilitaCampiProprietarioFisico(false);
        }
        propDivSel?.updateValueAndValidity({ emitEvent: false });
        // Forziamo il controllo su tutti i campi del blocco proprietario
        campiPropFisico.forEach(c => c?.updateValueAndValidity({ emitEvent: false }));
      });

    // Abilita i campi del nuovo proprietario solo se proprietarioDiversoSelect === 0;
    // se è un proprietario esistente (id > 0): popola i campi in readonly senza svuotarli
    propDivSel?.valueChanges
      .pipe(startWith(propDivSel.value), takeUntil(this.destroy$))
      .subscribe((v) => {
        const codiceCtrl = fF.get('codiceIdentificativoFisico');
        const sitoCtrl = fF.get('sitoWebProprietario');
        if (v === 0) {
          abilitaCampiProprietarioFisico(true);
          codiceCtrl?.enable({ emitEvent: false });
          sitoCtrl?.enable({ emitEvent: false });
        } else if (v !== null && v !== undefined && v !== '') {
          // Proprietario esistente: rimuovi required ma non svuotare i campi
          abilitaCampiProprietarioFisico(false, false);
          codiceCtrl?.disable({ emitEvent: false });
          sitoCtrl?.disable({ emitEvent: false });
          // Popola i campi dai rawProprietari se già caricati
          const raw = this.rawProprietari.find((p: any) => p.id === v);
          if (raw) this.patchProprietarioFields(raw);
        } else {
          abilitaCampiProprietarioFisico(false, true);
          codiceCtrl?.disable({ emitEvent: false });
          sitoCtrl?.disable({ emitEvent: false });
        }
      });

    // I campi di catalogazione aggiuntivi sono facoltativi e si svuotano solo quando non applicabili.
    const catalogoCtrl = fF.get('catalogatoInventariatoStato');
    const campiCatalogazione = [
      fF.get('condivisaCon'),
      fF.get('sistemiInformatizzati'),
      fF.get('inventariCartacei'),
    ];

    catalogoCtrl?.valueChanges
      .pipe(startWith(catalogoCtrl.value), takeUntil(this.destroy$))
      .subscribe((v) => {
        const shouldShow = v !== '' && v !== null && v !== 'CA002';
        campiCatalogazione.forEach(c => {
          c?.removeValidators(Validators.required);
          c?.setErrors(null);
          if (shouldShow) {
            c?.enable({ emitEvent: false });
          } else {
            c?.setValue(c === fF.get('condivisaCon') ? [] : '');
            c?.markAsPristine();
            c?.disable({ emitEvent: false });
          }
          c?.updateValueAndValidity({ emitEvent: false });
        });
      });

    // --- tipologiaFisico: mostra cognome solo se persona fisica (PR004)
    const tipologiaFisicoCtrl = fF.get('tipologiaFisico');
    const cognomeFisicoCtrl = fF.get('propCognomeFisico');

    tipologiaFisicoCtrl?.valueChanges
      .pipe(startWith(tipologiaFisicoCtrl.value), takeUntil(this.destroy$))
      .subscribe((v) => {
        const isNuovo = fF.get('proprietarioDiversoSelect')?.value === 0;
        const needCognome = isNuovo && String(v) === 'PR004';
        this.toggleRequired(cognomeFisicoCtrl, needCognome);
        if (!needCognome) {
          cognomeFisicoCtrl?.setValue('');
          cognomeFisicoCtrl?.markAsPristine();
        }
        cognomeFisicoCtrl?.updateValueAndValidity({ emitEvent: false });
      });

    // --- 5) blocco "Visualizzazione mappa"
    const mappaCtrl = this.visualizzazioneMappa.get('collezioneMappa');
    const indirizzoMappaCtrl = this.visualizzazioneMappa.get('indirizzoMappa');

    mappaCtrl?.valueChanges
      .pipe(startWith(mappaCtrl.value), takeUntil(this.destroy$))
      .subscribe((v) => {
        const isSi = v === 'si';
        this.toggleRequired(indirizzoMappaCtrl, isSi);
        if (!isSi) {
          indirizzoMappaCtrl?.setValue('');
          indirizzoMappaCtrl?.markAsPristine();
        }
        indirizzoMappaCtrl?.updateValueAndValidity({ emitEvent: false });
      });

    // Imposta ordine step in base al flag useModal
    const isModal = this.submitLogic.useModal();
    if (isModal) {
      this.steps.set([
        { key: 'digitale', label: 'Collezione digitale' },
        { key: 'anagrafici', label: 'Dati anagrafici' },
        { key: 'mappa', label: 'Visualizzazione mappa' },
      ]);
    } else {
      this.steps.set([
        { key: 'digitale', label: 'Collezione digitale' },
        { key: 'anagrafici', label: 'Dati anagrafici' },
        { key: 'mappa', label: 'Visualizzazione mappa' },
      ]);
    }

    // Detect edit mode: se idCollezioneModal ha un id, carica la collezione completa
    const stored = this.idCollezioneModal();
    if (stored?.id && stored.id !== 1) {
      this.isEditMode.set(true);
      this.loadCollezioneForEdit(stored.id);
    }

  }


  proprietarioDiversoValue(): boolean {
    let value = this.patrimonioFisico.get('proprietarioDiverso')?.value;
    return value === 'si' ? true : false;
  }

  disableFirstSection() {
    // a monte di una chiamata api per prendere user data si disabilitano le seguenti
    // api call qui
    this.formDataAnagrafica?.get('nome')?.disable();
    this.formDataAnagrafica?.get('cognome')?.disable();
    this.formDataAnagrafica?.get('email')?.disable();
    this.formDataAnagrafica?.get('enteAppartenenza')?.disable();
    this.formDataAnagrafica?.get('professioneRuolo')?.disable();
  }

  /**
   * Popola tutti i form con i dati della collezione (modalità edit).
   * Chiamato dopo getCollezioneById quando si apre una collezione esistente.
   */
  populateFormsFromResponse(el: mainSrc): void {
    const prop = el.datiAnagrafici?.proprietarioCollezione;
    if (prop) {
      this.formDataAnagrafica.patchValue({
        // Persona fisica: il campo mostra il Nome; altri enti: mostra il Nominativo.
        propNome: prop.tipologia?.codice === 'PR004' ? (prop.nome ?? '') : (prop.nominativo ?? prop.nome ?? ''),
        propCognome: prop.cognome ?? '',
        tipologia: prop.tipologia?.codice ?? '',
        categoria: (prop.categorie as any)?.[0]?.codice ?? (prop.categorie as any)?.codice ?? '',
        codiceIdentificativo: prop.codiceIdentificativo ?? '',
        indirizzo: prop.indirizzo?.indirizzo ?? '',
        civico: prop.indirizzo?.civico ?? '',
        cap: prop.indirizzo?.cap ?? '',
        comune: prop.indirizzo?.comune ?? '',
        provincia: prop.indirizzo?.provincia ?? '',
        sitoWeb: prop.sitoWeb ?? '',
      });
      this.idProprietario.set(prop.id ?? 0);

      if (prop.indirizzo) {
        this.addStoredIndirizzo(prop.indirizzo);
      }
    }

    // step 2 — Collezione digitale
    const boolToSiNo = (v: boolean | null | undefined): string =>
      v === true ? 'si' : v === false ? 'no' : '';

    this.collectionDescrizione.set(el.descrizione ?? '');

    this.formDataDigitale.patchValue({
      titolo: el.titolo ?? '',
      descrizione: el.descrizione ?? '',
      tipologiaBeni: el.tipologieBeni?.map(t => t?.codice).filter(Boolean) ?? [],
      tipologiaRisorseDigitali: el.tipologieRisorse?.map(t => t?.codice).filter(Boolean) ?? [],
      presenzaMetadati: el.metadati ?? '',
      note: el.note ?? '',
      accessibileOnline: boolToSiNo(el.accesso?.accessoOnline),
      url: el.accesso?.url ?? '',
      accessibileLoco: boolToSiNo(el.accesso?.accessoInLoco),
      modalitaAccesso: el.accesso?.modalitaAccesso ?? '',
      patrimonioNonAccessibile: el.accesso?.patrimonioNonAccessibile ?? '',
      soluzioniNazionali: boolToSiNo(el.accesso?.recuperoEsposizione),
      licenza: el.accesso?.licenze?.[0]?.codice ?? '',
      progetto: el.attivita?.progetto ?? '',
      ...(() => {
        const periodoStr = el.attivita?.periodo ?? '';
        const toYM = (s: string): string => {
          const trimmed = s.trim();
          if (!trimmed) return '';
          const parts = trimmed.split('/');
          return parts.length === 2 ? `${parts[1]}-${parts[0].padStart(2, '0')}` : '';
        };
        const split = periodoStr.split(' - ');
        return { periodoFrom: toYM(split[0] ?? ''), periodoTo: toYM(split[1] ?? '') };
      })(),
      statoAvanzamento: el.attivita?.statoAvanzamento?.codice ?? '',
      nativaDigitale: boolToSiNo(el.attivita?.collezioneNativa),
    });

    // step 3 — Patrimonio Fisico (se presente)
    const info = el.infoPatrimonio;
    if (info) {
      this.patrimonioFisico.patchValue({
        descrizioneFisico: info.descrizione ?? '',
        dominio: info.dominio?.map(d => d?.codice).filter(Boolean) ?? [],
        proprietarioDiverso: boolToSiNo(info.proprietarioDiverso),
        condizioniAccesso: info.accesso?.condizioniAccesso ?? '',
        indirizzoPatrimonioFisico: info.accesso?.indirizzo?.indirizzoEsteso ?? info.accesso?.indirizzo?.indirizzo ?? '',
        indirizzoPatrimonioFisicoId: info.accesso?.indirizzo?.id ?? null,
        civicoPatrimonioFisico: info.accesso?.indirizzo?.civico ?? '',
        capPatrimonioFisico: info.accesso?.indirizzo?.cap ?? '',
        comunePatrimonioFisico: info.accesso?.indirizzo?.comune ?? '',
        provinciaPatrimonioFisico: info.accesso?.indirizzo?.provincia ?? '',
        catalogatoInventariatoStato: info.catalogazione?.stato?.codice ?? '',
        condivisaCon: this.parseCondivisaCon(info.catalogazione?.condivisaCon),
        sistemiInformatizzati: info.catalogazione?.sistemiInformatizzati ?? '',
        inventariCartacei: info.catalogazione?.inventariCartacei ?? '',
      });

      if (info.accesso?.indirizzo) {
        this.addStoredIndirizzo(info.accesso.indirizzo);
        // Se c'è già un indirizzo salvato, mostro readonly invece della search
        if (info.accesso.indirizzo.indirizzo) {
          this.editingAddressPatrimonio.set(false);
        }
      }

      if (info.proprietarioDiverso && info.proprietario?.indirizzo) {
        this.addStoredIndirizzo(info.proprietario.indirizzo);
        if (info.proprietario.indirizzo.indirizzo) {
          this.editingAddressSedeLegale.set(false);
        }
      }

      if (info.proprietarioDiverso && info.proprietario?.id) {
        const p = info.proprietario;
        // Pre-carica l'opzione nel select anche se getAllProprietari() non è ancora tornato
        const label = `${p.nome ?? p.nominativo ?? ''} ${p.cognome ?? ''} - ${p.indirizzo?.indirizzo ?? ''}`.trim();
        if (!this.selectProprietari.some((sp: any) => sp.value === p.id)) {
          this.selectProprietari.push({ value: p.id, label });
        }
        // Precompila con i dati embedded nel response (evita schermo vuoto)
        this.patchProprietarioFields(p);
        this.patrimonioFisico.get('proprietarioDiversoSelect')?.setValue(p.id, { emitEvent: false });

        // Recupera i dati aggiornati dal BE tramite idEqual per avere tutti i campi
        this.submitLogic.getProprietarioById(p.id as number).subscribe({
          next: (res: any) => {
            const fresh = res.elements?.[0] ?? res.element ?? null;
            if (fresh) {
              // Aggiorna opzione nella combo con label fresco
              const freshLabel = `${fresh.nome ?? fresh.nominativo ?? ''} ${fresh.cognome ?? ''} - ${fresh.indirizzo?.indirizzo ?? ''}`.trim();
              const idx = this.selectProprietari.findIndex((sp: any) => sp.value === fresh.id);
              if (idx >= 0) this.selectProprietari[idx].label = freshLabel;
              // Aggiorna anche rawProprietari
              const rawIdx = this.rawProprietari.findIndex((rp: any) => rp.id === fresh.id);
              if (rawIdx >= 0) this.rawProprietari[rawIdx] = fresh;
              else this.rawProprietari.push(fresh);
              // Patch con dati freschi
              this.patchProprietarioFields(fresh);
            }
          },
        });
      }
    }

    // step 4 — Visualizzazione mappa
    const vis = el.visualizzazioneMappa;
    if (vis) {
      this.mappaCompleted.set(vis.mostraMappa === true);
      this.visualizzazioneMappa.patchValue({
        collezioneMappa: boolToSiNo(vis.mostraMappa),
        indirizzoMappa: vis.indirizzo?.id ?? '',
      });
      if (vis.indirizzo?.id) {
        this.addStoredIndirizzo(vis.indirizzo);
      }
      if (vis.immagini?.length) {
        const cover = vis.immagini.find(img => img?.isCopertina) ?? vis.immagini[0];
        if (cover?.logoImmagine) {
          this.file.set(cover.logoImmagine);
          this.uploadedFiles.set(
            vis.immagini.map((img, idx) => ({
              id: idx + 1,
              fileName: img?.isCopertina ? 'Immagine copertina' : `Immagine ${idx + 1}`,
              thumbnail: img?.logoImmagine ?? undefined,
              isCover: !!img?.isCopertina,
              _beId: img?.id ?? undefined,
              _beVersion: img?.version ?? undefined,
            }))
          );
        }
      }
    }
  }

  /**
   * Carica la collezione completa dal BE e popola tutti i form (modalità edit).
   */
  loadCollezioneForEdit(id: number): void {
    this.submitLogic.getCollezioneById(id).subscribe({
      next: (res: wizardResponse) => {
        this.mainRes = res;
        this.populateFormsFromResponse(res.element);
        // In edit mode i dati sono già sul server: considera tutti gli step non-mappa come già salvati
        const isPublished = res.element.visualizzazioneMappa?.mostraMappa === true;
        this.savedSteps.update(() => {
          const preloaded = new Set<StepKey>(
            this.steps().map(s => s.key)
          );
          if (!isPublished) preloaded.delete('mappa');
          return preloaded;
        });
        // Se la collezione è già pubblicata in mappa, apri direttamente lo step mappa
        if (isPublished) {
          const mappaIdx = this.steps().findIndex(s => s.key === 'mappa');
          if (mappaIdx >= 0) this.idx.set(mappaIdx);
        }
      },
    });
  }

  // step 1
  // prendiamo i dati dell utente, facciamo la setup del proprietario prendendo id della collezione creata in modale e modifichiamo in put la collezione
  getTempUser() {
    this.authService.myProfile().subscribe({
      next: (res: ResponseUser) => {
        console.log(res);
        this.user = res;

        const user = res.element;
        const ente = user.ente;

        this.formDataAnagrafica.patchValue({
          nome: user.nome,
          cognome: user.cognome,
          email: user.email,
          enteAppartenenza: ente?.nominativo ?? '',
          professioneRuolo: user.ruolo,
          // Persona fisica: il campo mostra il Nome; altri enti: mostra il Nominativo.
          propNome: ente?.tipologia?.codice === 'PR004' ? (ente?.nome ?? '') : (ente?.nominativo ?? ente?.nome ?? ''),
          propCognome: ente?.cognome ?? '',
          tipologia: ente?.tipologia?.codice ?? '',
          categoria: ente?.categorie?.[0]?.codice ?? '',
          codiceIdentificativo: ente?.codiceIdentificativo ?? '',
          indirizzo: ente?.indirizzo?.indirizzo ?? '',
          civico: ente?.indirizzo?.civico ?? '',
          cap: ente?.indirizzo?.cap ?? '',
          comune: ente?.indirizzo?.comune ?? '',
          provincia: ente?.indirizzo?.provincia ?? '',
          sitoWeb: ente?.sitoWeb ?? '',
        });
        if (ente?.indirizzo) {
          this.addStoredIndirizzo(ente.indirizzo);
        }
        this.getAllProprietari();
      },
    });
  }

  setupProprietario() {
    const fv = (n: string) => this.formDataAnagrafica.get(n)?.value;
    const tipoProprietario = fv('tipologia');

    this.proprietario = {
      ...(this.user.element.ente ?? {}),
      version: this.user.element.ente?.version ?? 0,
      id: this.user.element.ente?.id,
      // Persona fisica: nome + cognome (il BE calcola "nome cognome"); altri enti: solo nominativo.
      nominativo: tipoProprietario === 'PR004' ? null : fv('propNome'),
      nome: tipoProprietario === 'PR004' ? fv('propNome') : null,
      cognome: tipoProprietario === 'PR004' ? fv('propCognome') : null,
      tipologia: this.getFullTipologica('proprietario', tipoProprietario),
      categorie: [this.getFullTipologica('categoriaproprietario', fv('categoria'))],
      codiceIdentificativo: fv('codiceIdentificativo'),
      indirizzo: {
        ...(this.user.element.ente?.indirizzo ?? {}),
        indirizzo: fv('indirizzo'),
        civico: fv('civico'),
        cap: fv('cap'),
        comune: fv('comune'),
        provincia: fv('provincia'),
      },
      sitoWeb: fv('sitoWeb'),
    };

    this.submitLogic.updateProprietario(this.proprietario).subscribe({
      next: (res: any) => {
        if (res.element?.indirizzo) {
          this.addStoredIndirizzo(res.element.indirizzo);
        }

        this.authService.myProfile().subscribe({
          next: (freshUser: ResponseUser) => {
            this.user = freshUser;

            const currentId = this.idCollezioneModal()?.id;
            const existingDatiAnagrafici = this.mainRes?.element?.datiAnagrafici;

            let objModifica: mainSrc = {
              ...(this.mainRes?.element ?? {}),
              id: currentId,
              titolo: currentId ? this.idCollezioneModal().titolo : this.mainRes?.element?.titolo,
              datiAnagrafici: {
                ...(existingDatiAnagrafici ?? {}),
                soggettoCompilatore: {
                  ...this.user.element,
                  enteProprietario: this.user.element.ente,
                  nominativo: `${this.user.element.nome} ${this.user.element.cognome}`.trim(),
                },
                proprietarioCollezione: res.element,
              },
            };
            this.idProprietario.set(res.element.id);
            this.modificaCollezione(objModifica);
          },
        });
      },
    });
  }

  modificaCollezione(data: mainSrc) {
    if (!data.id) {
      this.submitLogic.setupTitolo(data).subscribe({
        next: (res: any) => {
          this.submitLogic.setResponse(res.element);
          this.mainRes = res;
          this.snackbar.show('Dati anagrafici salvati con successo', 'success');
        },
        error: () => this.snackbar.show('Errore nel salvataggio dei dati anagrafici', 'error'),
      });
    } else {
      this.submitLogic.modifyCollezione(data).subscribe({
        next: (res: any) => {
          this.mainRes = res;
          this.getCollezioneById(this.idCollezioneModal().id);
          this.snackbar.show('Dati anagrafici salvati con successo', 'success');
        },
        error: () => this.snackbar.show('Errore nel salvataggio dei dati anagrafici', 'error'),
      });
    }
  }

  // step 2
  // ci prendiamo la collezione dalla get by id e poi gestiamo tutto il form
  getCollezioneById(id: number) {
    this.submitLogic.getCollezioneById(id).subscribe({
      next: (res) => {
        // setup del form
        this.mainRes = res;
        this.formDataDigitale.get('titolo')?.setValue(res.element.titolo);
      },
    });
  }

  submitCollezioneDigitale(data: mainSrc) {
    const hasId = !!data.id;
    const call$ = hasId
      ? this.submitLogic.setupCollezioneDigitale(data)
      : this.submitLogic.setupTitolo(data);

    call$.subscribe({
      next: (res: wizardResponse) => {
        this.mainRes = res;
        if (!hasId) {
          this.submitLogic.setResponse(res.element);
        }
        this.snackbar.show('Collezione digitale salvata con successo', 'success');
      },
      error: () => this.snackbar.show('Errore nel salvataggio della collezione digitale', 'error'),
    });
  }

  setupCollezioneDigitale() {
    const currentId = this.idCollezioneModal()?.id ?? null;

    const buildAndSubmit = () => {
      const fv = (n: string) => this.formDataDigitale.get(n)?.value;
      const siNo = (n: string) => fv(n) === 'si';

      if (!this.mainRes) {
        this.mainRes = { element: {} } as wizardResponse;
      }

      this.mainRes = {
        ...this.mainRes,
        element: {
          ...this.mainRes.element,
          ...(currentId ? { id: currentId } : {}),

          ...this.onlyIfDef(fv('titolo'), { titolo: fv('titolo') }),
          ...this.onlyIfDef(fv('descrizione'), { descrizione: fv('descrizione') }),
          ...this.onlyIfDef(fv('presenzaMetadati'), {
            metadati: fv('presenzaMetadati'),
          }),
          ...this.onlyIfDef(fv('note'), { note: fv('note') }),

          ...this.onlyIfDef(fv('tipologiaBeni')?.length ? fv('tipologiaBeni') : null, {
            tipologieBeni: this.getFullTipologica('tipobene', fv('tipologiaBeni')),
          }),
          ...this.onlyIfDef(fv('tipologiaRisorseDigitali')?.length ? fv('tipologiaRisorseDigitali') : null, {
            tipologieRisorse: this.getFullTipologica('metadato', fv('tipologiaRisorseDigitali')),
          }),

          // accesso
          accesso: {
            ...(this.mainRes.element?.accesso ?? {}),

            ...this.onlyIfDef(fv('accessibileOnline'), {
              accessoOnline: siNo('accessibileOnline'),
            }),
            ...this.onlyIfDef(fv('url'), { url: fv('url') }),
            ...this.onlyIfDef(fv('accessibileLoco'), {
              accessoInLoco: siNo('accessibileLoco'),
            }),
            ...this.onlyIfDef(fv('modalitaAccesso'), {
              modalitaAccesso: fv('modalitaAccesso'),
            }),
            ...this.onlyIfDef(fv('soluzioniNazionali'), {
              recuperoEsposizione: siNo('soluzioniNazionali'),
            }),
            ...this.onlyIfDef(fv('licenza'), { licenze: [this.getFullTipologica('licenza', fv('licenza'))] }),
            ...this.onlyIfDef(fv('patrimonioNonAccessibile'), { patrimonioNonAccessibile: fv('patrimonioNonAccessibile') }),
          },

          // attivita
          attivita: {
            ...(this.mainRes.element?.attivita ?? {}),

            ...this.onlyIfDef(fv('progetto'), { progetto: fv('progetto') }),
            ...(() => {
              const toDisplay = (ym: string): string => {
                if (!ym) return '';
                const [y, m] = ym.split('-');
                return `${m}/${y}`;
              };
              const from = toDisplay(fv('periodoFrom') ?? '');
              const to = toDisplay(fv('periodoTo') ?? '');
              const periodo = from && to ? `${from} - ${to}` : (from || to || null);
              return this.onlyIfDef(periodo, { periodo });
            })(),
            ...this.onlyIfDef(fv('statoAvanzamento'), {
              statoAvanzamento: this.getFullTipologica('statoavanzamento', fv('statoAvanzamento')),
            }),
            ...this.onlyIfDef(fv('nativaDigitale'), {
              collezioneNativa: siNo('nativaDigitale'),
            }),
          },
        },
      };

      console.log(this.mainRes.element);

      this.submitCollezioneDigitale(this.mainRes.element);
    };

    if (currentId) {
      this.submitLogic.getCollezioneById(currentId).subscribe({
        next: (res) => {
          this.mainRes = res;
          buildAndSubmit();
        },
      });
    } else {
      buildAndSubmit();
    }
  }

  // setup 3
  // submit li dove il patrimonio è fisico

  setupProprietarioPatrimonioFisico(): ProprietarioCollezione {
    const tipoProprietario =
      this.patrimonioFisico.get('tipologiaFisico')?.value;

    const nomeFisico = this.patrimonioFisico?.get('propNomeFisico')?.value;
    return {
      // Persona fisica: nome + cognome (il BE calcola "nome cognome"); altri enti: solo nominativo.
      nome: tipoProprietario === 'PR004' ? nomeFisico : null,
      cognome:
        tipoProprietario === 'PR004'
          ? this.patrimonioFisico?.get('propCognomeFisico')?.value
          : null,
      nominativo: tipoProprietario === 'PR004' ? null : nomeFisico,
      tipologia: this.getFullTipologica('proprietario', tipoProprietario),
      categorie: [this.getFullTipologica('categoriaproprietario', this.patrimonioFisico?.get('categoriaFisico')?.value)],
      codiceIdentificativo: this.patrimonioFisico?.get(
        'codiceIdentificativoFisico'
      )?.value,
      indirizzo: this.resolveIndirizzoPayload(
        this.patrimonioFisico.get('indirizzoSedeLegaleFisicoId')?.value,
        this.patrimonioFisico.get('indirizzoSedeLegaleFisico')?.value,
        this.patrimonioFisico.get('civicoPatrimonioProprietario')?.value,
        this.patrimonioFisico.get('capPatrimonioProprietario')?.value,
        this.patrimonioFisico.get('comunePatrimonioProprietario')?.value,
        this.patrimonioFisico.get('provinciaPatrimonioProprietario')?.value,
      ),
      sitoWeb: this.patrimonioFisico?.get('sitoWebProprietario')?.value,
    };
  }

  selectProprietari: any[] = [
    {
      value: 0,
      label: 'Aggiungi nuovo proprietario',
    },
  ];
  rawProprietari: any[] = [];

  /**
   * Popola i form-controls del proprietario fisico a partire da un oggetto raw (da API o da wizardResponse).
   * Usato sia nel `valueChanges` (selezione da combo) sia nel `populateFormsFromResponse` (VIEW mode).
   */
  patchProprietarioFields(p: any): void {
    this.patrimonioFisico.patchValue({
      tipologiaFisico: p.tipologia?.codice ?? '',
      // Persona fisica: il campo mostra il Nome; altri enti: mostra il Nominativo.
      propNomeFisico: p.tipologia?.codice === 'PR004' ? (p.nome ?? '') : (p.nominativo ?? p.nome ?? ''),
      propCognomeFisico: p.cognome ?? '',
      categoriaFisico: p.categorie?.[0]?.codice ?? '',
      codiceIdentificativoFisico: p.codiceIdentificativo ?? '',
      indirizzoSedeLegaleFisico: p.indirizzo?.indirizzoEsteso ?? p.indirizzo?.indirizzo ?? '',
      indirizzoSedeLegaleFisicoId: p.indirizzo?.id ?? null,
      civicoPatrimonioProprietario: p.indirizzo?.civico ?? '',
      capPatrimonioProprietario: p.indirizzo?.cap ?? '',
      comunePatrimonioProprietario: p.indirizzo?.comune ?? '',
      provinciaPatrimonioProprietario: p.indirizzo?.provincia ?? '',
      sitoWebProprietario: p.sitoWeb ?? '',
    });
    // Se ha un indirizzo: passa in modalità readonly per indirizzoSedeLegaleFisico
    if (p.indirizzo?.indirizzo) {
      this.editingAddressSedeLegale.set(false);
    }
  }

  getAllProprietari() {
    const params = {
      idEqual: this.user.element.ente?.id,
    };
    this.submitLogic.getAllProprietari(params).subscribe({
      next: (res: any) => {
        this.rawProprietari = res.elements ?? [];
        (res.elements ?? []).forEach((prop: any) => {
          const exists = this.selectProprietari.some(p => p.value === prop.id);
          if (!exists) {
            this.selectProprietari.push({
              value: prop.id,
              label: `${prop.nome ?? prop.nominativo ?? ''} ${prop.cognome ?? ''} - ${prop.indirizzo?.indirizzo ?? ''}`.trim(),
            });
          }
        });
      },
    });
  }

  submitPatrimonioFisico(data: mainSrc) {
    this.submitLogic.setupPatrimonioFisico(data).subscribe({
      next: (res) => {
        this.mainRes = res;
        const indir = res.element.infoPatrimonio?.accesso?.indirizzo;
        this.addStoredIndirizzo(indir);
        this.snackbar.show('Patrimonio fisico salvato con successo', 'success');
      },
      error: () => this.snackbar.show('Errore nel salvataggio del patrimonio fisico', 'error'),
    });
  }

  // setup completo con merge oggetto dal be
  private buildPatrimonioPayloadAndSubmit() {
    const fv = (n: string) => this.patrimonioFisico.get(n)?.value;
    const siNo = (n: string) => fv(n) === 'si';
    const isCA002 = fv('catalogatoInventariatoStato') === 'CA002';
    const propDiverso = fv('proprietarioDiverso') === 'si';
    const proprietarioId = propDiverso
      ? fv('proprietarioDiversoSelect')
      : this.idProprietario();

    this.mainRes = {
      ...this.mainRes,
      element: {
        ...this.mainRes.element,
        infoPatrimonio: {
          ...(this.mainRes.element?.infoPatrimonio ?? {}),
          ...this.onlyIfDef(fv('descrizioneFisico'), {
            descrizione: fv('descrizioneFisico'),
          }),
          dominio: this.getFullTipologica('dominio', fv('dominio')),
          ...this.onlyIfDef(fv('proprietarioDiverso'), {
            proprietarioDiverso: siNo('proprietarioDiverso'),
          }),
          proprietario: propDiverso
            ? (this.rawProprietari.find(p => p.id === proprietarioId) || this.mainRes.element?.infoPatrimonio?.proprietario || { id: proprietarioId })
            : (this.mainRes.element?.datiAnagrafici?.proprietarioCollezione || this.mainRes.element?.infoPatrimonio?.proprietario || { id: proprietarioId }),
          accesso: {
            ...(this.mainRes.element?.infoPatrimonio?.accesso ?? {}),
            ...this.onlyIfDef(fv('condizioniAccesso'), {
              condizioniAccesso: fv('condizioniAccesso'),
            }),
            indirizzo: this.resolveIndirizzoPayload(
              fv('indirizzoPatrimonioFisicoId'),
              fv('indirizzoPatrimonioFisico'),
              fv('civicoPatrimonioFisico'),
              fv('capPatrimonioFisico'),
              fv('comunePatrimonioFisico'),
              fv('provinciaPatrimonioFisico'),
            ),
          },
          catalogazione: {
            ...(this.mainRes.element?.infoPatrimonio?.catalogazione ?? {}),
            ...this.onlyIfDef(fv('catalogatoInventariatoStato'), {
              stato: this.getFullTipologica('catalogazione', fv('catalogatoInventariatoStato')),
            }),
            ...(isCA002
              ? {
                condivisaCon: null,
                sistemiInformatizzati: null,
                inventariCartacei: null,
              }
              : {
                ...this.onlyIfDef(this.serializeCondivisaCon(fv('condivisaCon')), {
                  condivisaCon: this.serializeCondivisaCon(fv('condivisaCon')),
                }),
                ...this.onlyIfDef(fv('sistemiInformatizzati'), {
                  sistemiInformatizzati: fv('sistemiInformatizzati'),
                }),
                ...this.onlyIfDef(fv('inventariCartacei'), {
                  inventariCartacei: fv('inventariCartacei'),
                }),
              }),
          },
        },
      },
    };

    this.submitPatrimonioFisico(this.mainRes.element);
  }

  setupPatrimonioFisico() {
    const propDiverso = this.patrimonioFisico.get('proprietarioDiverso')?.value === 'si';
    const selValue = this.patrimonioFisico.get('proprietarioDiversoSelect')?.value;

    // Se proprietario diverso e selezione "Nuovo" (0): prima crea il proprietario via POST, poi salva il patrimonio
    if (propDiverso && selValue === 0) {
      const nuovoProprietario = this.setupProprietarioPatrimonioFisico();
      this.submitLogic.setupProprietario(nuovoProprietario).subscribe({
        next: (res: any) => {
          const newId = res.element.id;
          // Imposta l'id del nuovo proprietario nel campo select
          this.patrimonioFisico.get('proprietarioDiversoSelect')?.setValue(newId);

          // Aggiungi alla lista dei proprietari disponibili
          const label = `${res.element.nome ?? res.element.nominativo ?? ''} ${res.element.cognome ?? ''} - ${res.element.indirizzo?.indirizzo ?? ''}`.trim();
          if (!this.selectProprietari.some(p => p.value === newId)) {
            this.selectProprietari.push({ value: newId, label });
          }

          // Salva l'indirizzo per lo step Mappa
          if (res.element.indirizzo?.id) {
            this.addStoredIndirizzo(res.element.indirizzo);
          }

          // Ora che abbiamo l'id, procedi con il PUT della collezione
          this.buildPatrimonioPayloadAndSubmit();
        },
        error: () => this.snackbar.show('Errore nella creazione del proprietario', 'error'),
      });
      return;
    }

    // Proprietario esistente selezionato o non diverso: procedi direttamente con il PUT
    // Aggiunge l'indirizzo del proprietario diverso esistente a storedIndirizzi (per step Mappa)
    if (propDiverso && selValue && selValue !== 0) {
      const rawProp = this.rawProprietari.find((p: any) => p.id === selValue);
      if (rawProp?.indirizzo?.id) {
        this.addStoredIndirizzo(rawProp.indirizzo);
      }
    }
    this.buildPatrimonioPayloadAndSubmit();
  }

  // step 4
  submitVisMappa(data: mainSrc) {
    this.submitLogic.setupVisMappa(data).subscribe({
      next: (res: any) => {
        this.mainRes = res;
        this.mappaCompleted.set(res.element?.visualizzazioneMappa?.mostraMappa === true);
        this.snackbar.show('Dati mappa salvati con successo', 'success');
        const id = this.idCollezioneModal()?.id;
        if (id) {
          this.getCollezioneById(id);
        }
      },
      error: () => this.snackbar.show('Errore nel salvataggio dei dati mappa', 'error'),
    });
  }

  onUploadValidationError(message: string): void {
    this.snackbar.show(message, 'warning');
  }

  onInputFileReader(files: UploadedFile[]) {
    if (!files?.length) return;

    const currentCount = this.uploadedFiles().length;
    const remaining = this.maxCollectionImages - currentCount;

    if (remaining <= 0) {
      this.snackbar.show(`Puoi caricare al massimo ${this.maxCollectionImages} immagini`, 'warning');
      return;
    }

    const rawFiles = files.map(f => f.file).filter((file): file is File => !!file);
    const { valid, invalidTypes, invalidSizes } = validateCollectionImageFiles(rawFiles);

    if (invalidTypes.length) {
      this.snackbar.show(collectionImageTypeErrorMessage(invalidTypes), 'warning');
    }

    if (invalidSizes.length) {
      this.snackbar.show(collectionImageSizeErrorMessage(invalidSizes), 'warning');
    }

    if (!valid.length) {
      return;
    }

    const toProcess = valid.slice(0, remaining);
    if (toProcess.length < valid.length) {
      this.snackbar.show(
        `Puoi caricare al massimo ${this.maxCollectionImages} immagini: sono stati aggiunti solo ${toProcess.length} file`,
        'warning'
      );
    }

    toProcess.forEach((file, idx) => {
      const newFile: FileData = {
        id: Date.now() + idx,
        fileName: file.name,
        size: `${(file.size / (1024 * 1024)).toFixed(1)} MB`,
        isCover: currentCount === 0 && idx === 0,
      };
      this.uploadedFiles.update(list => [...list, newFile]);

      const reader = new FileReader();
      reader.onload = () => {
        const dataUrl = reader.result as string;
        this.uploadedFiles.update(list =>
          list.map(f => f.id === newFile.id ? { ...f, thumbnail: dataUrl } : f)
        );
        const cover = this.uploadedFiles().find(f => f['isCover']);
        if (cover?.['thumbnail']) {
          this.file.set(cover['thumbnail'] as string);
        }
      };
      reader.readAsDataURL(file);
    });
  }

  onSetCover(id: string | number) {
    this.uploadedFiles.update(list =>
      list.map(f => ({ ...f, isCover: f.id === id }))
    );
    const cover = this.uploadedFiles().find(f => f['isCover']);
    if (cover?.['thumbnail']) {
      this.file.set(cover['thumbnail'] as string);
    }
  }

  onDeleteFile(event: any) {
    const deletedId = event.file.id;
    const wasCover = !!(event.file as FileData & { isCover?: boolean }).isCover;
    this.uploadedFiles.update(list => {
      const remaining = list.filter(f => f.id !== deletedId);
      // se il file eliminato era la copertina, assegna al primo rimasto
      if (wasCover && remaining.length > 0) {
        remaining[0] = { ...remaining[0], isCover: true };
      }
      return remaining;
    });
    const cover = this.uploadedFiles().find(f => f['isCover']);
    if (cover?.['thumbnail']) {
      this.file.set((cover['thumbnail'] as string).split(',')[1] ?? null);
    } else {
      this.file.set(null);
    }
  }

  setupVisMappa() {
    const currentId = this.idCollezioneModal()?.id ?? null;

    const buildAndSubmit = () => {
      const fv = (n: string) => this.visualizzazioneMappa.get(n)?.value;
      const isSi = fv('collezioneMappa') === 'si';
      const indirizzoId = fv('indirizzoMappa');

      this.mainRes = {
        ...this.mainRes,
        element: {
          ...this.mainRes.element,
          visualizzazioneMappa: {
            ...(this.mainRes.element?.visualizzazioneMappa ?? {}),
            ...this.onlyIfDef(fv('collezioneMappa'), {
              mostraMappa: isSi,
            }),
            ...this.onlyIfDef(this.uploadedFiles().length ? this.uploadedFiles() : null, {
              immagini: this.uploadedFiles().map(f => ({
                ...(f['_beId'] != null ? { id: f['_beId'] as number, version: (f['_beVersion'] as number) ?? 0 } : {}),
                logoImmagine: (f['thumbnail'] as string) ?? '',
                isCopertina: !!f['isCover'],
              })),
            }),
            // indirizzo incluso solo se mostraMappa = true e id non vuoto
            indirizzo: isSi ? (this.storedIndirizzi().find(i => i.value === (indirizzoId?.id || indirizzoId))?.data
              || this.mainRes.element?.visualizzazioneMappa?.indirizzo
              || (indirizzoId ? (indirizzoId.id ? indirizzoId : { id: indirizzoId }) : null)) : null,
          },
        },
      };
      this.submitVisMappa(this.mainRes.element);
    };

    if (currentId) {
      this.submitLogic.getCollezioneById(currentId).subscribe({
        next: (res) => {
          this.mainRes = res;
          buildAndSubmit();
        },
      });
    } else {
      buildAndSubmit();
    }
  }
}
